import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { OnboardingTaskRow } from "./use-partner-onboarding";

export type TogglePartnerTaskInput = {
  taskId: string;
  done: boolean;
};

type Context = {
  previous: OnboardingTaskRow[] | undefined;
};

// PATCH /api/portal/onboarding/:taskId
//
// optimistic: status flip is the user-visible reflex; defer DB confirmation.
// The all-done ONBOARDING_COMPLETED Slack ping fires on the worker via
// executionCtx.waitUntil — invisible to the SPA.
//
// Pattern C (state-flip) — see MIGRATION.md "Optimistic-update reference".
// Same shape works for archive/restore, primary/non-primary, assigned/
// unassigned flips in future buckets.
export function useTogglePartnerTask() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, TogglePartnerTaskInput, Context>({
    mutationFn: ({ taskId, done }) =>
      apiFetch<{ ok: true }>(`/portal/onboarding/${taskId}`, {
        method: "PATCH",
        body: JSON.stringify({ done }),
      }),
    onMutate: async ({ taskId, done }) => {
      const key = queryKeys.portal.onboarding();
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<OnboardingTaskRow[]>(key);
      queryClient.setQueryData<OnboardingTaskRow[]>(key, (old) =>
        old?.map((t) =>
          t.id === taskId
            ? {
                ...t,
                status: done ? "completed" : "pending",
                completed_at: done ? new Date().toISOString() : null,
              }
            : t,
        ),
      );
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKeys.portal.onboarding(), context.previous);
      }
      toast.error(err.message ?? "Failed to update task");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.onboarding() });
    },
  });
}
